import { logger } from '@/utils/logger'

/**
 * CSRF Token Storage Manager
 *
 * Handles cross-tab synchronization of CSRF tokens using localStorage and BroadcastChannel.
 * This ensures all tabs share the same CSRF token to prevent session conflicts.
 */

interface CSRFTokenData {
  token: string
  expiresAt: number
  lastUpdated: number
}

export interface TokenUpdateMessage {
  type: 'csrf-token-update' | 'csrf-token-request' | 'csrf-token-clear' | 'csrf-token-response'
  token?: string
  expiresAt?: number
  requestId?: string
}

export class CSRFTokenStorage {
  private static readonly STORAGE_KEY = 'ai-hack-csrf-token'
  private static readonly TOKEN_TTL = 3600000 // 1 hour in milliseconds
  private static readonly BROADCAST_CHANNEL_NAME = 'ai-hack-csrf-sync'

  private broadcastChannel: BroadcastChannel | null = null
  private tokenRequestInProgress = false
  private tokenRequestPromise: Promise<string | null> | null = null
  private listeners: Set<(token: string | null) => void> = new Set()
  private pendingTokenRequests: Map<
    string,
    {
      resolve: (token: string | null) => void
      timeoutId: NodeJS.Timeout
    }
  > = new Map()

  constructor() {
    this.initializeBroadcastChannel()
  }

  /**
   * Initialize BroadcastChannel for cross-tab communication
   */
  private initializeBroadcastChannel(): void {
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        this.broadcastChannel = new BroadcastChannel(CSRFTokenStorage.BROADCAST_CHANNEL_NAME)

        this.broadcastChannel.onmessage = (event: MessageEvent<TokenUpdateMessage>) => {
          this.handleBroadcastMessage(event.data)
        }

        logger.debug('CSRF BroadcastChannel initialized')
      } else {
        logger.warn('BroadcastChannel not supported, falling back to localStorage only')
      }
    } catch (error) {
      logger.error('Failed to initialize BroadcastChannel:', error)
    }
  }

  /**
   * Handle messages from other tabs
   */
  private handleBroadcastMessage(message: TokenUpdateMessage): void {
    logger.debug('Received broadcast message:', { type: message.type })

    switch (message.type) {
      case 'csrf-token-update':
        if (message.token && message.expiresAt) {
          // Another tab updated the token, update our local storage
          this.saveTokenToStorage(message.token, message.expiresAt)
          this.notifyListeners(message.token)
        }
        break

      case 'csrf-token-clear':
        // Another tab cleared the token
        this.clearStorage()
        this.notifyListeners(null)
        break

      case 'csrf-token-request': {
        // Another tab is requesting the token, share if we have a valid one
        const token = this.getTokenFromStorage()
        if (token && message.requestId) {
          // Send a direct response to the requesting tab
          this.broadcastTokenResponse(token.token, token.expiresAt, message.requestId)
        }
        break
      }

      case 'csrf-token-response': {
        // Handle response to our token request
        if (message.requestId && message.token) {
          const pendingRequest = this.pendingTokenRequests.get(message.requestId)
          if (pendingRequest) {
            clearTimeout(pendingRequest.timeoutId)
            pendingRequest.resolve(message.token)
            this.pendingTokenRequests.delete(message.requestId)
          }
        }
        break
      }
    }
  }

  /**
   * Broadcast token update to other tabs
   */
  private broadcastTokenUpdate(token: string, expiresAt: number): void {
    if (this.broadcastChannel) {
      const message: TokenUpdateMessage = {
        type: 'csrf-token-update',
        token,
        expiresAt,
      }

      try {
        this.broadcastChannel.postMessage(message)
        logger.debug('Broadcasted token update to other tabs')
      } catch (error) {
        logger.error('Failed to broadcast token update:', error)
      }
    }
  }

  /**
   * Broadcast token response to a specific request
   */
  private broadcastTokenResponse(token: string, expiresAt: number, requestId: string): void {
    if (this.broadcastChannel) {
      const message: TokenUpdateMessage = {
        type: 'csrf-token-response',
        token,
        expiresAt,
        requestId,
      }

      try {
        this.broadcastChannel.postMessage(message)
        logger.debug('Broadcasted token response to requesting tab', { requestId })
      } catch (error) {
        logger.error('Failed to broadcast token response:', error)
      }
    }
  }

  /**
   * Broadcast token clear to other tabs
   */
  private broadcastTokenClear(): void {
    if (this.broadcastChannel) {
      const message: TokenUpdateMessage = {
        type: 'csrf-token-clear',
      }

      try {
        this.broadcastChannel.postMessage(message)
        logger.debug('Broadcasted token clear to other tabs')
      } catch (error) {
        logger.error('Failed to broadcast token clear:', error)
      }
    }
  }

  /**
   * Get token from localStorage
   */
  private getTokenFromStorage(): CSRFTokenData | null {
    try {
      const data = localStorage.getItem(CSRFTokenStorage.STORAGE_KEY)
      if (!data) {
        return null
      }

      const tokenData: CSRFTokenData = JSON.parse(data)

      // Check if token is expired
      if (tokenData.expiresAt < Date.now()) {
        logger.debug('CSRF token expired, clearing')
        this.clearStorage()
        return null
      }

      return tokenData
    } catch (error) {
      logger.error('Failed to get token from storage:', error)
      return null
    }
  }

  /**
   * Save token to localStorage
   */
  private saveTokenToStorage(token: string, expiresAt?: number): void {
    try {
      const tokenData: CSRFTokenData = {
        token,
        expiresAt: expiresAt || Date.now() + CSRFTokenStorage.TOKEN_TTL,
        lastUpdated: Date.now(),
      }

      localStorage.setItem(CSRFTokenStorage.STORAGE_KEY, JSON.stringify(tokenData))
      logger.debug('CSRF token saved to storage')
    } catch (error) {
      logger.error('Failed to save token to storage:', error)
    }
  }

  /**
   * Clear token from localStorage
   */
  private clearStorage(): void {
    try {
      localStorage.removeItem(CSRFTokenStorage.STORAGE_KEY)
      logger.debug('CSRF token cleared from storage')
    } catch (error) {
      logger.error('Failed to clear token from storage:', error)
    }
  }

  /**
   * Notify all listeners of token change
   */
  private notifyListeners(token: string | null): void {
    this.listeners.forEach(listener => {
      try {
        listener(token)
      } catch (error) {
        logger.error('Error in token listener:', error)
      }
    })
  }

  /**
   * Get current CSRF token
   */
  public getToken(): string | null {
    const tokenData = this.getTokenFromStorage()
    return tokenData?.token || null
  }

  /**
   * Set CSRF token and broadcast to other tabs
   */
  public setToken(token: string, expiresAt?: number): void {
    this.saveTokenToStorage(token, expiresAt)
    this.broadcastTokenUpdate(token, expiresAt || Date.now() + CSRFTokenStorage.TOKEN_TTL)
    this.notifyListeners(token)

    logger.info('CSRF token updated and broadcasted')
  }

  /**
   * Clear CSRF token and broadcast to other tabs
   */
  public clearToken(): void {
    this.clearStorage()
    this.broadcastTokenClear()
    this.notifyListeners(null)

    logger.info('CSRF token cleared and broadcasted')
  }

  /**
   * Add listener for token changes
   */
  public addListener(listener: (token: string | null) => void): () => void {
    this.listeners.add(listener)

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Check if a token request is in progress
   */
  public isTokenRequestInProgress(): boolean {
    return this.tokenRequestInProgress
  }

  /**
   * Set token request state
   */
  public setTokenRequestInProgress(inProgress: boolean): void {
    this.tokenRequestInProgress = inProgress
  }

  /**
   * Get or set the token request promise for deduplication
   */
  public getTokenRequestPromise(): Promise<string | null> | null {
    return this.tokenRequestPromise
  }

  public setTokenRequestPromise(promise: Promise<string | null> | null): void {
    this.tokenRequestPromise = promise
  }

  /**
   * Atomically get both the token request promise and progress state
   * This prevents race conditions when checking both values
   */
  public getTokenRequestState(): {
    promise: Promise<string | null> | null
    inProgress: boolean
  } {
    return {
      promise: this.tokenRequestPromise,
      inProgress: this.tokenRequestInProgress,
    }
  }

  /**
   * Request token from other tabs if available
   */
  public async requestTokenFromTabs(): Promise<string | null> {
    return new Promise(resolve => {
      // First check local storage
      const localToken = this.getToken()
      if (localToken) {
        resolve(localToken)
        return
      }

      if (!this.broadcastChannel) {
        resolve(null)
        return
      }

      // Generate unique request ID
      const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

      // Set up timeout
      const timeoutId = setTimeout(() => {
        const pendingRequest = this.pendingTokenRequests.get(requestId)
        if (pendingRequest) {
          pendingRequest.resolve(null)
          this.pendingTokenRequests.delete(requestId)
          logger.debug('Token request timed out', { requestId })
        }
      }, 100) // 100ms timeout

      // Store the pending request
      this.pendingTokenRequests.set(requestId, {
        resolve,
        timeoutId,
      })

      // Send request to other tabs
      const message: TokenUpdateMessage = {
        type: 'csrf-token-request',
        requestId,
      }

      try {
        this.broadcastChannel.postMessage(message)
        logger.debug('Requested token from other tabs', { requestId })
      } catch (error) {
        logger.error('Failed to request token from other tabs:', error)
        // Clean up on error
        clearTimeout(timeoutId)
        this.pendingTokenRequests.delete(requestId)
        resolve(null)
      }
    })
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    // Clean up any pending token requests
    this.pendingTokenRequests.forEach(({ timeoutId, resolve }) => {
      clearTimeout(timeoutId)
      resolve(null)
    })
    this.pendingTokenRequests.clear()

    if (this.broadcastChannel) {
      this.broadcastChannel.close()
      this.broadcastChannel = null
    }
    this.listeners.clear()
    this.tokenRequestPromise = null
  }
}

// Export singleton instance
export const csrfTokenStorage = new CSRFTokenStorage()
